import React from 'react';
import PropTypes from 'prop-types';

import UFOSegment from '@atlaskit/react-ufo/segment';

import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { PageSegmentLoadMark } from '@confluence/browser-metrics/entry-points/PageSegmentLoadMark.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy, LoadablePaint } from '@confluence/loadable';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';
import { PAGE_COMMENTS_METRIC } from '@confluence/comments-section/entry-points/pageSegmentLoadMetrics.ts';
import { useAllowNestedComments } from '@confluence/comments-hooks/entry-points/useAllowNestedComments.ts';
import { useCommentCounts } from '@confluence/comments-hooks/entry-points/useCommentCounts.ts';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';

/* if handlePageCommentsRender is passed from ViewPageFooterComments we handle
displaying CommentPlaceholder in that component till comments are rendered*/

//@ts-ignore
/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * It was used to manage legacy page comments but is no longer needed as part of modernization.
 */
export const CommentsSectionFabric: LoadableType<Omit<any, 'client'>> = LoadableLazy({
	// eslint-disable-next-line react/prop-types
	//@ts-ignore
	loading: ({ handlePageCommentsRender }) => {
		return !handlePageCommentsRender ? <CommentPlaceholder /> : null;
	},
	loader: () =>
		//@ts-ignore
		import(/* webpackChunkName: "loadable-CommentsSectionFabric" */ './CommentsSectionFabric.jsx'),
});

export const CommentsSectionFabricPaint: LoadableType<Omit<any, 'client'>> = LoadablePaint({
	loader: () =>
		//@ts-ignore
		import(/* webpackChunkName: "loadable-CommentsSectionFabric" */ './CommentsSectionFabric.jsx'),
});

//@ts-ignore
export const CommentsSectionLoader: {
	({ contentId, ...props }: { [x: string]: any; contentId: any }): React.JSX.Element;
	propTypes: {
		contentId: PropTypes.Validator<string>;
	};
} = ({ contentId, ...props }: { [x: string]: any; contentId: any }): React.JSX.Element => {
	// This information is available in the CommentsCountContext, but it also needs to be available to the legacy class
	// component so passing in directly from the hook as well
	const { unresolvedInlineCommentsCount } = useCommentCounts(contentId);
	const { cloudId } = useSessionData();
	const allowNestedComments = useAllowNestedComments();
	const commentsDataActions = useCommentsDataActions();

	const { refetch: refetchActiveComments } = useActiveCommentsQuery({
		pageId: contentId,
	});

	return (
		// @ts-ignore
		<UFOSegment name="page-comments">
			<CommentCountsProvider contentId={contentId}>
				<PageSegmentLoadStart key={props.contentId} metric={PAGE_COMMENTS_METRIC} isCustomStart />
				<PageSegmentLoadMark markName="pageCommentsRenderStart" metric={PAGE_COMMENTS_METRIC} />
				{fg('confluence_frontend_remove_comments_placeholder') ? (
					<CommentsSectionFabricPaint
						{...props}
						// @ts-ignore
						contentId={contentId}
						inlineCommentsCount={unresolvedInlineCommentsCount}
						cloudId={cloudId}
						allowNestedComments={allowNestedComments}
						commentsDataActions={commentsDataActions}
						refetchCommentsPanel={refetchActiveComments}
					/>
				) : (
					<CommentsSectionFabric
						{...props}
						// @ts-ignore
						contentId={contentId}
						inlineCommentsCount={unresolvedInlineCommentsCount}
						cloudId={cloudId}
						allowNestedComments={allowNestedComments}
						commentsDataActions={commentsDataActions}
						refetchCommentsPanel={refetchActiveComments}
					/>
				)}
			</CommentCountsProvider>
		</UFOSegment>
	);
};

CommentsSectionLoader.propTypes = {
	contentId: PropTypes.string.isRequired,
};
